import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Copy, Download, RefreshCw, Save, Trash2 } from "lucide-react";
import {
  ApiError,
  backfillAdminHls,
  cleanupAdminJobs,
  getAdminSettings,
  startAdminPerformanceCapture,
  updateAdminSettings,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import { coerceBool } from "@/lib/coerce";
import { TkticSelect } from "@/components/ui/TkticSelect";
import type {
  AdminJob,
  AdminPerformance,
  AdminSettingsPayload,
  TuningKnobSchema,
} from "@/lib/types";
import {
  useAdminDiagnostics,
  useAdminJobs,
  useAdminPerformance,
} from "../hooks/useAdminData";
import { AdminIconButton } from "../components/AdminIconButton";
import { AdminPageHeader } from "../components/AdminPageHeader";

/**
 * M8a D1–D9 — `/admin/performance`.
 *
 * Three full-width cards:
 *   1. Encoder & host tile grid (D1)
 *   2. Tuning knobs grid (D2–D5) — rendered from the server's
 *      `tuning_knobs` schema so a backend knob addition flows through
 *      without a client edit. Bool → checkbox, int → number input,
 *      enum → select, JSON → JSON textarea (D3 ladder gets a friendly
 *      editor only when it's the `hls_variant_presets` key).
 *   3. Diagnostics accordions (D6) — Recent Errors / Active Sessions /
 *      Audit. Each accordion is a controlled <button>+<div> expander,
 *      NOT native <details>, per the no-native-chrome rule.
 *
 * Save flow (D8): the tuning PUT is scoped to ONLY the tuning knobs
 * payload, NOT a shared settings handler — matching the documented
 * legacy gotcha (the legacy `handleSettingsSubmit` read live-form
 * inputs and silently cleared live config if Live/Settings was
 * never visited).
 *
 * 5 s `useAdminPerformance` powers tile values + capture status.
 * The audit feed reads from `useAdminSettings.audit`. The 10 s
 * `useAdminDiagnostics` from the shell exposes the upload sessions
 * + failed slots + recent_activity events used in D6.
 */
export function AdminPerformancePage() {
  const perfQuery = useAdminPerformance({ refetchIntervalMs: 5_000 });
  const jobsQuery = useAdminJobs({ refetchIntervalMs: 10_000 });
  const diagQuery = useAdminDiagnostics();
  const settingsQuery = useQuery({
    queryKey: unscopedKey("admin-settings"),
    queryFn: getAdminSettings,
  });

  return (
    <div data-testid="admin-performance" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Operations"
        title="Processing"
        description="Watch the video pipeline: uploads, transcodes, HLS backlog, failed work, and the host capacity behind it."
        meta={perfQuery.isFetching ? "Refreshing..." : "Auto-refreshes every 5 s"}
      />

      <ProcessingStatusStrip
        diagnostics={diagQuery.data}
        jobCounts={jobsQuery.data?.counts ?? {}}
        perf={perfQuery.data}
      />
      <BackgroundJobsStrip jobs={jobsQuery.data?.jobs ?? []} counts={jobsQuery.data?.counts ?? {}} />
      <DiagnosticsBlock
        diagnostics={diagQuery.data}
        settings={settingsQuery.data}
      />
      <EncoderHostTiles perf={perfQuery.data} />
      <PerformanceCaptureCard perf={perfQuery.data} />
      <TuningKnobsCard
        settings={settingsQuery.data}
        loading={settingsQuery.isLoading}
        error={settingsQuery.error}
      />
    </div>
  );
}

function ProcessingStatusStrip({
  diagnostics,
  jobCounts,
  perf,
}: {
  diagnostics: ReturnType<typeof useAdminDiagnostics>["data"];
  jobCounts: Record<string, number>;
  perf: AdminPerformance | undefined;
}) {
  const failedSlots = diagnostics?.counts.failed_slots ?? 0;
  const hlsMissing = diagnostics?.counts.hls_missing_slots ?? 0;
  const activeUploads = (diagnostics?.upload_sessions ?? []).filter(
    (session) => session.status === "active",
  ).length;
  const runningJobs = jobCounts.running ?? 0;
  const pendingJobs = jobCounts.pending ?? 0;
  const failedJobs = jobCounts.failed ?? 0;
  const cpu = Number((perf?.host as Record<string, unknown> | undefined)?.cpu_percent ?? 0);
  const gpu = Number((perf?.host as Record<string, unknown> | undefined)?.intel_gpu_busy_pct ?? 0);
  const status =
    failedSlots > 0 || failedJobs > 0
      ? "Needs recovery"
      : runningJobs > 0 || activeUploads > 0
        ? "Processing"
        : "Idle";
  const tone =
    failedSlots > 0 || failedJobs > 0
      ? "bad"
      : runningJobs > 0 || activeUploads > 0
        ? "busy"
        : "good";

  return (
    <section
      data-testid="admin-processing-status"
      data-status={status.toLowerCase().replace(" ", "-")}
      className="rounded border border-rule bg-surface p-3 shadow-sm"
    >
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <ProcessingDot tone={tone} />
            <p className="font-display text-sm uppercase tracking-wider text-ink">
              {status}
            </p>
            <span className="rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] text-ink-muted">
              {runningJobs} running · {pendingJobs} pending
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            {tone === "bad"
              ? "A failed job or video slot needs operator recovery."
              : tone === "busy"
                ? "Video work is moving through uploads, transcodes, or background jobs."
                : "No active video-processing work is currently blocking the club."}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:flex xl:items-center">
          <ProcessingSignal label="Uploads" value={String(activeUploads)} tone={activeUploads > 0 ? "busy" : "idle"} />
          <ProcessingSignal label="Failed" value={String(failedSlots + failedJobs)} tone={failedSlots + failedJobs > 0 ? "bad" : "good"} />
          <ProcessingSignal label="HLS missing" value={String(hlsMissing)} tone={hlsMissing > 0 ? "warn" : "good"} />
          <ProcessingSignal label="Host load" value={`${cpu.toFixed(0)}% / ${gpu.toFixed(0)}%`} tone={cpu > 85 || gpu > 85 ? "warn" : "muted"} />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// D1 — Encoder & host tile grid
// ---------------------------------------------------------------------------

function EncoderHostTiles({ perf }: { perf: AdminPerformance | undefined }) {
  const host = (perf?.host ?? {}) as Record<string, unknown>;
  const disk = (perf?.disk ?? {}) as Record<string, unknown>;
  const tp = perf?.throughput;
  const tx = perf?.transcode;

  const ssd = (disk.ssd ?? {}) as Record<string, unknown>;
  const gpuSuccess = Number(getNested(tx?.gpu as any, ["successes"]) ?? 0);
  const gpuFail = Number(getNested(tx?.gpu as any, ["failures"]) ?? 0);

  return (
    <section
      data-testid="admin-perf-tiles"
      className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <Tile
        label="Live throughput"
        value={formatBps(Number(tp?.last?.bps_live_out ?? 0))}
        sub={`avg ${formatBps(Number(tp?.avg_live_bps_30s ?? 0))}`}
        testid="admin-perf-tile-live"
      />
      <Tile
        label="VOD throughput"
        value={formatBps(Number(tp?.last?.bps_vod_out ?? 0))}
        sub={`avg ${formatBps(Number(tp?.avg_vod_bps_30s ?? 0))}`}
        testid="admin-perf-tile-vod"
      />
      <Tile
        label="NIC TX/RX"
        value={`${formatBps(Number(getNested(host, ["net", "bps_tx"]) ?? 0))}`}
        sub={`rx ${formatBps(Number(getNested(host, ["net", "bps_rx"]) ?? 0))}`}
        testid="admin-perf-tile-nic"
      />
      <Tile
        label="CPU"
        value={`${Number(host.cpu_percent ?? 0).toFixed(0)}%`}
        testid="admin-perf-tile-cpu"
      />
      <Tile
        label="Memory"
        value={`${Number(getNested(host, ["memory", "percent"]) ?? 0).toFixed(0)}%`}
        testid="admin-perf-tile-memory"
      />
      <Tile
        label="iGPU busy"
        value={`${Number(host.intel_gpu_busy_pct ?? 0).toFixed(0)}%`}
        testid="admin-perf-tile-gpu"
      />
      <Tile
        label="SSD free"
        value={formatBytes(Number(ssd.free ?? 0))}
        sub={`of ${formatBytes(Number(ssd.total ?? 0))}`}
        testid="admin-perf-tile-ssd"
      />
      <Tile
        label="Encoder pipeline"
        value={String(tx?.concurrency_limit ?? 0)}
        sub={`gpu ok ${gpuSuccess} · fail ${gpuFail}`}
        testid="admin-perf-tile-encoder"
      />
    </section>
  );
}

function Tile({
  label,
  value,
  sub,
  testid,
}: {
  label: string;
  value: string;
  sub?: string;
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col rounded border border-rule bg-elevated p-2"
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="text-sm font-mono text-ink">{value}</span>
      {sub ? (
        <span className="text-[0.6rem] font-mono text-ink-muted">{sub}</span>
      ) : null}
    </div>
  );
}

function PerformanceCaptureCard({ perf }: { perf: AdminPerformance | undefined }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const capture = perf?.throughput.capture ?? {};
  const active = capture.fast === true;
  const remaining = Number(capture.remaining_seconds ?? 0);
  const snapshot = useMemo(
    () => JSON.stringify({ captured_at: new Date().toISOString(), performance: perf }, null, 2),
    [perf],
  );

  const start = useMutation({
    mutationFn: () => startAdminPerformanceCapture(60),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: unscopedKey("admin-performance") });
      toast.success("Performance capture started");
    },
    onError: (err) =>
      toast.error("Couldn't start performance capture", {
        description: formatErr(err),
      }),
  });

  const downloadSnapshot = () => {
    const blob = new Blob([snapshot], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `tktic-performance-snapshot-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      data-testid="admin-perf-capture"
      className="rounded border border-accent/25 bg-gradient-to-br from-accent/10 via-elevated to-surface p-3 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Performance Capture
          </h3>
          <p className="text-xs text-ink-muted">
            60 s high-frequency telemetry window with one-click JSON evidence.
          </p>
        </div>
        <span
          data-testid="admin-perf-capture-status"
          data-active={active ? "true" : "false"}
          className="rounded-full border border-rule bg-surface px-2 py-1 text-[0.65rem] font-mono uppercase tracking-wider text-ink-muted"
        >
          {active ? `Capture active · ${Math.ceil(remaining)}s` : "Idle"}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <AdminIconButton
          icon={Activity}
          label={start.isPending ? "Starting performance capture" : "Start 60 second capture"}
          data-testid="admin-perf-capture-start"
          onClick={() => start.mutate()}
          disabled={start.isPending}
          tone="accent"
          size="md"
        />
        <AdminIconButton
          icon={Copy}
          label={
            copyState === "copied"
              ? "Snapshot copied"
              : copyState === "error"
                ? "Copy snapshot failed"
                : "Copy snapshot"
          }
          data-testid="admin-perf-capture-copy"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(snapshot);
              setCopyState("copied");
            } catch {
              setCopyState("error");
            }
          }}
          size="md"
        />
        <AdminIconButton
          icon={Download}
          label="Download snapshot"
          data-testid="admin-perf-capture-download"
          onClick={downloadSnapshot}
          size="md"
        />
      </div>
    </section>
  );
}

function BackgroundJobsStrip({
  jobs,
  counts,
}: {
  jobs: AdminJob[];
  counts: Record<string, number>;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  const visible = jobs.slice(0, 5);
  const failedCount = counts.failed ?? 0;
  const cleanup = useMutation({
    mutationFn: () => cleanupAdminJobs({ status: "failed", limit: 500 }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: unscopedKey("admin-jobs") }),
        queryClient.invalidateQueries({ queryKey: unscopedKey("admin-diagnostics") }),
      ]);
      setCleanupConfirmOpen(false);
      toast.success("Failed jobs cleared", {
        description: `${result.deleted} failed job${result.deleted === 1 ? "" : "s"} removed from the queue log.`,
      });
    },
    onError: (err) =>
      toast.error("Couldn't clear failed jobs", {
        description: formatErr(err),
      }),
  });

  return (
    <section
      data-testid="admin-perf-jobs"
      className="rounded border border-rule bg-surface shadow-sm p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Background Jobs
          </h3>
          <p className="text-xs text-ink-muted">Global queue health across teams.</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <div className="flex flex-wrap gap-1 font-mono text-[0.6rem] uppercase tracking-wider">
            <span data-testid="admin-perf-jobs-count-running" className="rounded border border-accent/40 px-2 py-1 text-accent">
              running {counts.running ?? 0}
            </span>
            <span data-testid="admin-perf-jobs-count-pending" className="rounded border border-rule px-2 py-1 text-ink-muted">
              pending {counts.pending ?? 0}
            </span>
            <span data-testid="admin-perf-jobs-count-failed" className="rounded border border-destructive/40 px-2 py-1 text-destructive">
              failed {failedCount}
            </span>
          </div>
          <AdminIconButton
            icon={Trash2}
            label={cleanup.isPending ? "Clearing failed jobs" : "Clear failed jobs"}
            data-testid="admin-perf-jobs-clear-failed"
            onClick={() => setCleanupConfirmOpen(true)}
            disabled={failedCount === 0 || cleanup.isPending}
            tone="destructive"
          />
        </div>
      </div>
      {visible.length === 0 ? (
        <p className="mt-2 text-[0.65rem] font-mono text-ink-muted">No background jobs.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {visible.map((job) => (
            <li
              key={job.id}
              data-testid="admin-perf-job-row"
              className="grid grid-cols-[auto_auto_1fr_auto] gap-2 rounded border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono"
            >
              <span className="text-ink-muted">#{job.id}</span>
              <span className={job.status === "running" ? "text-accent" : "text-ink"}>{job.status}</span>
              <span className="truncate text-ink">{job.kind}</span>
              <span className="text-ink-muted">{job.team_id}</span>
            </li>
          ))}
        </ul>
      )}
      {cleanupConfirmOpen ? (
        <ConfirmDialog
          title="Clear failed jobs?"
          message={`This removes up to 500 failed background-job rows from the operator queue log. It will not touch pending or running work.`}
          confirmLabel={cleanup.isPending ? "Clearing…" : "Clear failed jobs"}
          onConfirm={() => cleanup.mutate()}
          onClose={() => setCleanupConfirmOpen(false)}
          data-testid="admin-perf-jobs-clear-confirm"
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// D2–D5 — Tuning knobs card
// ---------------------------------------------------------------------------

function TuningKnobsCard({
  settings,
  loading,
  error,
}: {
  settings: AdminSettingsPayload | undefined;
  loading: boolean;
  error: unknown;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const tuningKnobs = settings?.tuning_knobs ?? {};
  const liveSettings = (settings?.settings ?? {}) as Record<string, unknown>;
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Dirty check compares normalized values per knob kind. Backend
  // returns bool knobs as the strings "0" / "1" — without
  // normalization, `liveSettings.live_record_enabled === "0"` would
  // always !== `draft.live_record_enabled === false` and a freshly-
  // mounted (un-edited) checkbox would already show as dirty. See
  // also the bool-rendering fix in `KnobInput` (re-review P1).
  const dirty = useMemo(
    () =>
      Object.keys(draft).filter((k) => {
        if (draft[k] === undefined) return false;
        const kind = tuningKnobs[k]?.kind;
        return (
          normalizeKnobValue(kind, draft[k]) !==
          normalizeKnobValue(kind, liveSettings[k])
        );
      }),
    [draft, liveSettings, tuningKnobs],
  );

  // D8 — dedicated tuning-only save. We send ONLY the knobs the
  // operator changed, never the full settings table.
  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateAdminSettings(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-settings"),
      });
      setDraft({});
      toast.success("Tuning knobs saved");
    },
    onError: (err) => {
      setSubmitError(formatErr(err));
      toast.error("Couldn't save tuning knobs", {
        description: formatErr(err),
      });
    },
  });

  // D9 — backfill HLS one-shot.
  const [backfillConfirmOpen, setBackfillConfirmOpen] = useState(false);
  const backfill = useMutation({
    mutationFn: backfillAdminHls,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-diagnostics"),
      });
      setBackfillConfirmOpen(false);
      toast.success("HLS backfill started");
    },
    onError: (err) => {
      setSubmitError(formatErr(err));
      toast.error("Couldn't start HLS backfill", {
        description: formatErr(err),
      });
    },
  });

  return (
    <section
      data-testid="admin-perf-tuning"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Tuning Knobs
        </h3>
        <div className="flex items-center gap-1">
          <AdminIconButton
            icon={RefreshCw}
            label="Backfill HLS"
            onClick={() => setBackfillConfirmOpen(true)}
            data-testid="admin-perf-backfill-hls"
          />
          <AdminIconButton
            icon={Save}
            label={save.isPending ? "Saving tuning changes" : `Save tuning changes${dirty.length ? ` (${dirty.length})` : ""}`}
            onClick={() => {
              setSubmitError(null);
              save.mutate(
                Object.fromEntries(dirty.map((k) => [k, draft[k]])),
              );
            }}
            disabled={dirty.length === 0 || save.isPending}
            data-testid="admin-perf-tuning-save"
            tone="accent"
          />
        </div>
      </header>

      {loading ? (
        <p className="text-xs font-mono text-ink-muted">Loading tuning schema…</p>
      ) : error ? (
        <p
          data-testid="admin-perf-tuning-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(error)}
        </p>
      ) : null}

      {submitError ? (
        <p
          data-testid="admin-perf-tuning-submit-error"
          className="text-destructive font-mono text-xs"
        >
          {submitError}
        </p>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {Object.entries(tuningKnobs).map(([key, schema]) => (
          <KnobField
            key={key}
            keyName={key}
            schema={schema}
            currentValue={
              draft[key] !== undefined ? draft[key] : liveSettings[key]
            }
            originalValue={liveSettings[key]}
            onChange={(next) => setDraft((d) => ({ ...d, [key]: next }))}
          />
        ))}
      </div>

      {backfillConfirmOpen ? (
        <ConfirmDialog
          title="Backfill HLS variants?"
          message="Rebuilds HLS for every ready-but-no-HLS slot. Can take a long time on a large library."
          confirmLabel={backfill.isPending ? "Backfilling…" : "Run backfill"}
          onConfirm={() => {
            setSubmitError(null);
            backfill.mutate();
          }}
          onClose={() => setBackfillConfirmOpen(false)}
          data-testid="admin-perf-backfill-confirm"
        />
      ) : null}
    </section>
  );
}

function KnobField({
  keyName,
  schema,
  currentValue,
  originalValue,
  onChange,
}: {
  keyName: string;
  schema: TuningKnobSchema;
  currentValue: unknown;
  originalValue: unknown;
  onChange: (next: unknown) => void;
}) {
  const dirty =
    normalizeKnobValue(schema.kind, currentValue) !==
    normalizeKnobValue(schema.kind, originalValue);
  const label = schema.label ?? keyName;
  const restart = schema.restart_required;

  return (
    <div
      data-testid={`admin-perf-knob-${keyName}`}
      data-knob-kind={schema.kind}
      data-knob-dirty={dirty ? "true" : undefined}
      className="flex flex-col gap-1 rounded border border-rule bg-elevated p-2"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[0.65rem] font-display uppercase tracking-wider text-ink">
          {label}
        </span>
        {restart ? (
          <span className="rounded border border-accent text-accent px-1 py-0 text-[0.55rem] font-mono">
            restart
          </span>
        ) : null}
      </div>
      {schema.description ? (
        <span className="text-[0.6rem] text-ink-muted">
          {schema.description}
        </span>
      ) : null}
      <KnobInput
        schema={schema}
        value={currentValue}
        onChange={onChange}
        testid={`admin-perf-knob-${keyName}-input`}
      />
    </div>
  );
}

function KnobInput({
  schema,
  value,
  onChange,
  testid,
}: {
  schema: TuningKnobSchema;
  value: unknown;
  onChange: (next: unknown) => void;
  testid: string;
}) {
  switch (schema.kind) {
    case "bool": {
      const boolOn = coerceBool(value ?? schema.default);
      return (
        <button
          type="button"
          onClick={() => onChange(!boolOn)}
          data-testid={testid}
          data-active={boolOn ? "true" : undefined}
          className="rounded border px-2 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
        >
          {boolOn ? "Enabled" : "Disabled"}
        </button>
      );
    }
    case "int":
      return (
        <input
          type="number"
          value={String(value ?? schema.default ?? 0)}
          min={schema.min}
          max={schema.max}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange(Number.isFinite(n) ? n : 0);
          }}
          data-testid={testid}
          className="rounded border border-rule bg-canvas px-2 py-1 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent"
        />
      );
    case "enum":
      return (
        <TkticSelect
          value={String(value ?? schema.default ?? "")}
          onChange={(next) => onChange(next)}
          data-testid={testid}
          size="sm"
          ariaLabel="Setting value"
          options={schema.choices.map((c) => ({ value: c, label: c }))}
        />
      );
    case "json":
      return (
        <textarea
          value={
            typeof value === "string"
              ? value
              : JSON.stringify(value ?? schema.default ?? null, null, 2)
          }
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          data-testid={testid}
          spellCheck={false}
          className="rounded border border-rule bg-canvas px-2 py-1 text-[0.65rem] font-mono focus:outline-none focus:ring-2 focus:ring-accent"
        />
      );
  }
}

// ---------------------------------------------------------------------------
// D6 — Diagnostics block
// ---------------------------------------------------------------------------

function DiagnosticsBlock({
  diagnostics,
  settings,
}: {
  diagnostics: ReturnType<typeof useAdminDiagnostics>["data"];
  settings: AdminSettingsPayload | undefined;
}) {
  const errors = diagnostics?.recent_errors ?? [];
  const failedSlots = diagnostics?.failed_slots ?? [];
  const uploads = diagnostics?.upload_sessions ?? [];
  const audit = settings?.audit ?? [];

  const [openSection, setOpenSection] = useState<
    "errors" | "failed" | "uploads" | "audit" | null
  >(null);

  return (
    <section
      data-testid="admin-perf-diag"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <h3 className="text-sm font-display uppercase tracking-wider text-ink">
        Diagnostics
      </h3>
      <ExpanderRow
        label="Recent errors"
        count={errors.length}
        open={openSection === "errors"}
        onToggle={() =>
          setOpenSection((s) => (s === "errors" ? null : "errors"))
        }
        testid="admin-perf-diag-errors"
      >
        {errors.length === 0 ? (
          <DiagEmpty />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {errors.map((e, i) => (
              <li
                key={i}
                data-testid="admin-perf-diag-row"
                data-diag-row="error"
                className="rounded border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono"
              >
                <div className="text-ink-muted">
                  {String(e.timestamp ?? "")} · match {String(e.match_id ?? "—")}/{String(e.slot ?? "—")}
                </div>
                <div className="text-ink">
                  <span className="text-destructive">{String(e.error_code ?? "error")}</span>{" "}
                  {String(e.reason ?? "")}
                </div>
              </li>
            ))}
          </ul>
        )}
      </ExpanderRow>

      <ExpanderRow
        label="Failed slots"
        count={failedSlots.length}
        open={openSection === "failed"}
        onToggle={() =>
          setOpenSection((s) => (s === "failed" ? null : "failed"))
        }
        testid="admin-perf-diag-failed"
      >
        {failedSlots.length === 0 ? (
          <DiagEmpty />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {failedSlots.map((s, i) => (
              <li
                key={`${s.match_id}-${s.slot}-${i}`}
                data-testid="admin-perf-diag-row"
                data-diag-row="failed"
                className="rounded border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono"
              >
                <span className="text-ink">{s.match_id}</span>
                <span className="text-ink-muted"> · {s.slot}</span>
                <span className="text-ink-muted"> · {s.home_team} vs {s.away_team}</span>
              </li>
            ))}
          </ul>
        )}
      </ExpanderRow>

      <ExpanderRow
        label="Upload sessions"
        count={uploads.length}
        open={openSection === "uploads"}
        onToggle={() =>
          setOpenSection((s) => (s === "uploads" ? null : "uploads"))
        }
        testid="admin-perf-diag-uploads"
      >
        {uploads.length === 0 ? (
          <DiagEmpty />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {uploads.map((u, i) => (
              <li
                key={`${u.session_id}-${i}`}
                data-testid="admin-perf-diag-row"
                data-diag-row="upload"
                className="rounded border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono"
              >
                <span className="text-ink">{u.match_id}/{u.slot}</span>
                <span className="text-ink-muted"> · {u.status}</span>
                {u.progress_pct !== undefined ? (
                  <span className="text-ink-muted">
                    {" "}· {u.progress_pct.toFixed(0)}%
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </ExpanderRow>

      <ExpanderRow
        label="Audit (knob changes)"
        count={audit.length}
        open={openSection === "audit"}
        onToggle={() =>
          setOpenSection((s) => (s === "audit" ? null : "audit"))
        }
        testid="admin-perf-diag-audit"
      >
        {audit.length === 0 ? (
          <DiagEmpty />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {audit.map((a, i) => (
              <li
                key={`${a.timestamp}-${i}`}
                data-testid="admin-perf-diag-row"
                data-diag-row="audit"
                className="rounded border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono"
              >
                <span className="text-ink-muted">{a.timestamp}</span>{" "}
                <span className="text-ink">{a.key}</span>{" "}
                <span className="text-ink-muted">by {a.actor}</span>{" "}
                <span className="text-ink-muted">
                  · {JSON.stringify(a.old_value)} → {JSON.stringify(a.new_value)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </ExpanderRow>
    </section>
  );
}

/** Controlled-state expander row — replaces the legacy <details>
 * accordion per the no-native-chrome rule. */
function ExpanderRow({
  label,
  count,
  open,
  onToggle,
  children,
  testid,
}: {
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      data-expander-open={open ? "true" : "false"}
      className="rounded border border-rule/60 bg-elevated"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-xs"
      >
        <span className="text-ink">{label}</span>
        <span className="text-ink-muted font-mono">
          {count} {open ? "▼" : "▶"}
        </span>
      </button>
      {open ? <div className="px-2 pb-2">{children}</div> : null}
    </div>
  );
}

function DiagEmpty() {
  return (
    <p className="text-[0.6rem] font-mono text-ink-muted px-1 py-1">
      No entries.
    </p>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

type ProcessingTone = "good" | "warn" | "bad" | "busy" | "idle" | "muted";

function ProcessingSignal({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: ProcessingTone;
}) {
  const classes: Record<ProcessingTone, string> = {
    good: "text-good",
    warn: "text-accent",
    bad: "text-destructive",
    busy: "text-accent",
    idle: "text-ink-muted",
    muted: "text-ink",
  };
  return (
    <div className="rounded border border-rule bg-elevated px-2 py-1.5">
      <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className={`font-mono text-xs ${classes[tone]}`}>{value}</span>
    </div>
  );
}

function ProcessingDot({ tone }: { tone: ProcessingTone }) {
  const classes: Record<ProcessingTone, string> = {
    good: "bg-good",
    warn: "bg-accent",
    bad: "bg-destructive",
    busy: "bg-accent",
    idle: "bg-ink-muted",
    muted: "bg-ink-muted",
  };
  return (
    <span
      aria-hidden="true"
      className={`mt-1 h-2 w-2 shrink-0 rounded-full ${classes[tone]}`}
    />
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}

function formatBps(bps: number): string {
  if (!Number.isFinite(bps) || bps <= 0) return "0 bps";
  const units = ["bps", "Kbps", "Mbps", "Gbps"];
  let v = bps;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function getNested(obj: unknown, path: string[]): unknown {
  let cur: unknown = obj;
  for (const k of path) {
    if (cur && typeof cur === "object" && k in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[k];
    } else {
      return undefined;
    }
  }
  return cur;
}

/** Re-review P1: coerce a tuning-knob value into a real boolean.
 *
 * The legacy `settings` table stores bools as the strings "0" / "1"
 * (and historical rows occasionally carry "true" / "false" / "on" /
 * "off"). `Boolean("0")` is `true`, so without this helper the
 * checkbox renders an off knob as on, the dirty check is permanently
 * tripped on every render, and toggling back to off PUT s `false` —
 * the operator sees a UI that contradicts the server state.
 *
 * `coerceBool` moved to `web/src/lib/coerce.ts` (M11 lift — public
 * viewer + admin share). The truthy/falsy/fallback rules are
 * documented there.
 */

/** Per-kind normalizer used by the dirty check. Returns a primitive
 * the equality operator can compare cleanly without false positives
 * from string-vs-typed-value mismatches. */
function normalizeKnobValue(
  kind: TuningKnobSchema["kind"] | undefined,
  value: unknown,
): unknown {
  if (kind === "bool") return coerceBool(value);
  if (kind === "int") {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  if (kind === "enum") {
    return value == null ? "" : String(value);
  }
  if (kind === "json") {
    // The textarea stores a string; the server returns the parsed
    // JSON. Stringify both sides for a stable compare.
    try {
      return typeof value === "string"
        ? JSON.stringify(JSON.parse(value))
        : JSON.stringify(value ?? null);
    } catch {
      return typeof value === "string" ? value : JSON.stringify(value ?? null);
    }
  }
  return value;
}
